import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Plus } from "lucide-react";
import {
  ORDER_STATUSES,
  ORDER_STATUS_DEFINITIONS,
  type OrderStatus,
} from "@laundrypro/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorText,
  PageHeader,
  SearchInput,
  SkeletonTableRows,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { currencyLabel, dateOnly, dateTime, money } from "../lib/format";

interface OrderListItem {
  id: string;
  orderRef: string;
  customerName: string | null;
  customerMobile: string | null;
  status: OrderStatus;
  receivedAt: string;
  expectedReadyAt: string | null;
  totalMinor: number;
  amountPaidMinor: number;
  balanceDueMinor: number;
  createdAt: string;
}

/**
 * Orders.
 *
 * The status filter is a row of chips above the table, one per state in the
 * machine, mirroring how the counter actually thinks: "show me what's ready".
 * The link to creation is an inline control on the card, not a floating button.
 */
export function OrdersPage() {
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  const [items, setItems] = useState<OrderListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | null>(null);

  const load = useCallback(
    async (term: string, status: OrderStatus | null) => {
      setLoadError(null);
      try {
        const params = new URLSearchParams({ pageSize: "50" });
        if (term) params.set("search", term);
        if (status) params.set("status", status);
        const data = await apiFetch<{ items: OrderListItem[] }>(`/orders?${params}`);
        setItems(data.items);
      } catch (err) {
        setItems([]);
        setLoadError(
          err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الطلبات." : "Couldn't load orders.",
        );
      }
    },
    [ar],
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(search.trim(), statusFilter), 250);
    return () => clearTimeout(timer);
  }, [search, statusFilter, load]);

  const loading = items === null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={ar ? "الطلبات" : "Orders"}
        subtitle={ar ? "طلبات الغسيل وحالتها ومستحقاتها" : "Laundry orders, their status and balances"}
      />

      <Card padding="none">
        <div className="flex flex-wrap items-center gap-3 p-5 pb-0 sm:p-6 sm:pb-0">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={ar ? "ابحث برقم الطلب أو العميل أو الجوال" : "Search order number, customer or mobile"}
          />
          {hasPermission("orders.create") && (
            // The Button primitive wrapped in a Link, so the control IS the
            // primitive — not a hand-rolled copy of its classes that drifts the
            // first time Button changes.
            <Link to="/orders/new" className="shrink-0">
              <Button type="button" icon={<Plus className="size-4" />} className="pointer-events-none h-10">
                {ar ? "طلب جديد" : "New order"}
              </Button>
            </Link>
          )}
        </div>

        {/* Status chips: the segmented-selection idiom, one chip per machine
            state plus "all". Filtering happens server-side. */}
        <div className="flex flex-wrap gap-2 p-5 pb-0 sm:p-6 sm:pb-0">
          <button
            type="button"
            onClick={() => setStatusFilter(null)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              statusFilter === null
                ? "border-accent-400 bg-accent-50 text-accent-600 ring-1 ring-inset ring-accent-200"
                : "border-line-strong text-ink-600 hover:bg-ink-50"
            }`}
          >
            {ar ? "الكل" : "All"}
          </button>
          {ORDER_STATUSES.map((status) => {
            const def = ORDER_STATUS_DEFINITIONS[status];
            const active = statusFilter === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(active ? null : status)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "border-accent-400 bg-accent-50 text-accent-600 ring-1 ring-inset ring-accent-200"
                    : "border-line-strong text-ink-600 hover:bg-ink-50"
                }`}
              >
                {ar ? def.labelAr : def.labelEn}
              </button>
            );
          })}
        </div>

        {loadError && (
          <div className="p-5 sm:p-6">
            <ErrorText>{loadError}</ErrorText>
          </div>
        )}

        {!loading && items.length === 0 && !loadError ? (
          <EmptyState
            icon={<ClipboardList className="size-6" />}
            title={ar ? "لا توجد طلبات بعد" : "No orders yet"}
            description={
              search || statusFilter
                ? ar
                  ? "لا نتائج تطابق هذا البحث أو الفلتر."
                  : "Nothing matches that search or filter."
                : ar
                  ? "أنشئ أول طلب من زر «طلب جديد» أعلاه."
                  : "Take in your first order with the New order button above."
            }
          />
        ) : (
          <div className="p-5 sm:p-6">
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "رقم الطلب" : "Order"}</Th>
                  <Th>{ar ? "العميل" : "Customer"}</Th>
                  <Th>{ar ? "التاريخ" : "Date"}</Th>
                  <Th>{ar ? "الجاهزية" : "Ready by"}</Th>
                  <Th>{ar ? "الحالة" : "Status"}</Th>
                  <Th>{ar ? "الإجمالي" : "Total"}</Th>
                  <Th>{ar ? "المدفوع" : "Paid"}</Th>
                  <Th>{ar ? "المستحق" : "Balance"}</Th>
                </tr>
              </THead>
              <tbody>
                {loading ? (
                  <SkeletonTableRows rows={6} cols={8} />
                ) : (
                  items.map((order) => {
                    const def = ORDER_STATUS_DEFINITIONS[order.status];
                    return (
                      <Tr key={order.id}>
                        <Td className="tabular-nums font-medium text-ink-900">
                          <Link to={`/orders/${order.id}`} className="hover:text-accent-600 hover:underline">
                            {order.orderRef}
                          </Link>
                        </Td>
                        <Td className="font-medium text-ink-900">{order.customerName ?? "-"}</Td>
                        <Td className="tabular-nums text-muted-foreground">
                          {dateTime(order.receivedAt, language)}
                        </Td>
                        <Td className="tabular-nums text-muted-foreground">
                          {dateOnly(order.expectedReadyAt, language)}
                        </Td>
                        <Td>
                          <Badge tone={def.tone}>{ar ? def.labelAr : def.labelEn}</Badge>
                        </Td>
                        <Td className="tabular-nums font-medium">
                          {money(order.totalMinor)} {currency}
                        </Td>
                        <Td className="tabular-nums">
                          {money(order.amountPaidMinor)} {currency}
                        </Td>
                        <Td className="tabular-nums">
                          {order.balanceDueMinor > 0 ? (
                            <span className="font-medium text-warning">
                              {money(order.balanceDueMinor)} {currency}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">0.00 {currency}</span>
                          )}
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
